import React, { useState, useEffect } from 'react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

/**
 * All Feedback Sources - Platform Admin
 * 
 * Following 00-READ-THIS-FIRST:
 * - NO MOCK DATA: Uses real /api/ml/feedback/sources endpoint
 * - NULL SAFETY: Handles missing data gracefully
 * - PROGRESSIVE COMPLEXITY: Simple display, can enhance later
 */

interface FeedbackSource {
  source: string;
  display_name: string;
  description: string;
  enabled: boolean;
  feedback_count: number;
}

export const AllFeedbackSources: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sources, setSources] = useState<FeedbackSource[]>([]);

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const token = localStorage.getItem('astrapeak_platform_admin_token');
        if (!token) {
          setError('No authentication token found');
          setLoading(false);
          return;
        }

        const response = await fetch(`${getBaseUrl()}/api/ml/feedback/sources`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error('Failed to fetch feedback sources');
        
        const data = await response.json();
        setSources(data.sources || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching feedback sources:', err);
        setError(err instanceof Error ? err.message : 'Failed to load feedback sources');
      } finally {
        setLoading(false);
      }
    };

    fetchSources();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white mb-2">All Feedback Sources</h1>
        <p className="text-gray-400">View and manage all feedback sources across the platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sources.map((source) => (
          <div key={source.source} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{source.display_name}</h3>
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                source.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
              }`}>
                {source.enabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">{source.description}</p>
            <div className="text-2xl font-semibold text-white">
              {source.feedback_count.toLocaleString()}
              <span className="text-sm text-gray-400 font-normal ml-2">items</span>
            </div>
          </div>
        ))}
      </div>

      {sources.length === 0 && (
        <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
          <p className="text-gray-400">No feedback sources configured</p>
        </div>
      )}
    </div>
  );
};

export default AllFeedbackSources;
